import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Edit, Trash2, Youtube, Play, Upload, Loader2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['News', 'Media Coverage', 'Events'];

// Helper to convert YouTube URL to embed format
const convertYouTubeUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/shorts\/([^&\s?]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
  }
  return url;
};

const emptyForm = {
  title: '',
  description: '',
  video_type: 'youtube',
  video_url: '',
  thumbnail_url: '',
  category: 'News',
  is_featured: false,
  order: 99,
};

const AdminNewsMedia = () => {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/news-media`);
      setItems(response.data);
    } catch (error) {
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'order') v = parseInt(value || '99', 10);
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const openModal = (item = null) => {
    if (item) {
      setEditing(item);
      setFormData({ ...emptyForm, ...item });
    } else {
      setEditing(null);
      setFormData(emptyForm);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditing(null);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a valid video file');
      return;
    }
    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error('Video must be 10MB or smaller. For larger videos, paste a YouTube URL instead.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload-video`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData((prev) => ({ ...prev, video_url: response.data.url, video_type: 'uploaded' }));
      toast.success(`Video uploaded (${response.data.size})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload-image`, fd, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      setFormData((prev) => ({ ...prev, thumbnail_url: response.data.url }));
      toast.success('Thumbnail uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.video_url) {
      toast.error('Please provide a YouTube URL or upload a video');
      return;
    }
    const submitData = {
      ...formData,
      // Auto-detect type from URL
      video_type: formData.video_url.startsWith('data:') ? 'uploaded' : 'youtube',
      video_url: convertYouTubeUrl(formData.video_url),
    };
    try {
      if (editing) {
        await axios.put(`${API}/news-media/${editing.id}`, submitData, { headers: getAuthHeader() });
        toast.success('News item updated');
      } else {
        await axios.post(`${API}/news-media`, submitData, { headers: getAuthHeader() });
        toast.success('News item added');
      }
      fetchItems();
      closeModal();
    } catch (error) {
      toast.error('Failed to save news item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this news/media item?')) return;
    try {
      await axios.delete(`${API}/news-media/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="admin-news-media-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-playfair font-bold text-stone-900 mb-2">News & Media</h1>
          <p className="text-stone-600">Publish project announcements, press coverage, and event videos</p>
        </div>
        <Button
          onClick={() => openModal()}
          data-testid="add-news-media-button"
          className="bg-emerald-900 hover:bg-emerald-800 text-white px-6 py-3 uppercase tracking-widest text-xs font-bold"
        >
          <Plus className="w-4 h-4 mr-2" /> ADD NEWS / MEDIA
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <div
            key={item.id}
            data-testid={`news-media-item-${item.id}`}
            className="bg-white border border-stone-200 overflow-hidden flex flex-col"
          >
            <div className="aspect-video bg-stone-900 relative">
              {item.video_type === 'youtube' ? (
                <iframe
                  src={item.video_url}
                  title={item.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video controls className="w-full h-full object-contain bg-black" preload="metadata">
                  <source src={item.video_url} />
                </video>
              )}
              <div className="absolute top-2 left-2 flex gap-1.5">
                {item.video_type === 'youtube' ? (
                  <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] rounded font-medium flex items-center gap-1">
                    <Youtube className="w-3 h-3" /> YouTube
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-blue-600 text-white text-[10px] rounded font-medium flex items-center gap-1">
                    <Play className="w-3 h-3" /> Uploaded
                  </span>
                )}
                {item.is_featured && (
                  <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] rounded font-medium flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" /> Featured
                  </span>
                )}
              </div>
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <span className={`self-start px-2 py-0.5 text-[10px] rounded font-medium mb-2 ${
                item.category === 'News' ? 'bg-blue-100 text-blue-700' :
                item.category === 'Media Coverage' ? 'bg-purple-100 text-purple-700' :
                'bg-green-100 text-green-700'
              }`}>
                {item.category}
              </span>
              <h3 className="font-bold text-stone-900 line-clamp-2 mb-1">{item.title}</h3>
              {item.description && (
                <p className="text-stone-500 text-sm line-clamp-2 mb-3">{item.description}</p>
              )}
              <div className="flex gap-2 mt-auto pt-3 border-t border-stone-100">
                <Button
                  onClick={() => openModal(item)}
                  variant="outline"
                  size="sm"
                  data-testid={`edit-news-media-${item.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button
                  onClick={() => handleDelete(item.id)}
                  variant="destructive"
                  size="sm"
                  data-testid={`delete-news-media-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-20 bg-white border border-stone-200">
          <p className="text-stone-500">No news or media items yet. Add the first one!</p>
        </div>
      )}

      <Dialog open={isModalOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="news-media-form-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-playfair font-bold">
              {editing ? 'Edit News / Media' : 'Add News / Media'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              name="title"
              placeholder="Title *"
              value={formData.title}
              onChange={handleChange}
              required
              data-testid="news-media-title-input"
            />
            <Textarea
              name="description"
              placeholder="Description (optional)"
              value={formData.description || ''}
              onChange={handleChange}
              rows={3}
              data-testid="news-media-description-input"
            />

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                data-testid="news-media-category-input"
                className="w-full h-12 px-3 border border-stone-300 focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 rounded"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">YouTube Video URL</label>
                <Input
                  name="video_url"
                  placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  value={formData.video_url}
                  onChange={handleChange}
                  className="bg-white"
                  data-testid="news-media-video-url-input"
                />
                <p className="text-xs text-blue-600 mt-1">Paste any YouTube URL - it will be auto-converted to embed format</p>
              </div>

              <div className="relative flex items-center gap-3">
                <div className="flex-1 h-px bg-blue-200" />
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-widest">OR</span>
                <div className="flex-1 h-px bg-blue-200" />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-800 mb-2">Upload Video from Computer</label>
                <label
                  htmlFor="news-media-video-upload"
                  data-testid="news-media-video-upload-label"
                  className={`flex items-center justify-center gap-2 h-12 px-4 rounded border-2 border-dashed cursor-pointer transition-colors ${
                    uploading
                      ? 'border-blue-400 bg-blue-100 text-blue-500 cursor-wait'
                      : 'border-blue-400 bg-white text-blue-700 hover:bg-blue-100'
                  }`}
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Choose video file</>
                  )}
                </label>
                <input
                  id="news-media-video-upload"
                  data-testid="news-media-video-upload-input"
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  disabled={uploading}
                  className="hidden"
                />
                <p className="text-xs text-blue-600 mt-1">MP4, WebM, MOV. Max 10MB. For longer videos, paste a YouTube URL above.</p>
              </div>

              {formData.video_url && (
                <div className="mt-2 aspect-video bg-stone-900 rounded overflow-hidden">
                  {formData.video_url.startsWith('data:') ? (
                    <video controls className="w-full h-full object-contain bg-black" preload="metadata">
                      <source src={formData.video_url} />
                    </video>
                  ) : (
                    <iframe
                      src={convertYouTubeUrl(formData.video_url)}
                      title="Video Preview"
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              )}

              {formData.video_url && (
                <button
                  type="button"
                  onClick={() => setFormData((p) => ({ ...p, video_url: '' }))}
                  data-testid="news-media-video-clear"
                  className="text-xs text-red-600 hover:text-red-800 font-medium underline"
                >
                  Remove video
                </button>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Thumbnail Image URL (optional)</label>
              <Input
                name="thumbnail_url"
                placeholder="https://... or upload below"
                value={formData.thumbnail_url || ''}
                onChange={handleChange}
                data-testid="news-media-thumbnail-input"
              />
              <label
                htmlFor="news-media-thumb-upload"
                className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded border border-stone-300 cursor-pointer hover:bg-stone-50"
              >
                <Upload className="w-3 h-3" /> Upload thumbnail
              </label>
              <input
                id="news-media-thumb-upload"
                type="file"
                accept="image/*"
                onChange={handleThumbnailUpload}
                disabled={uploading}
                className="hidden"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={!!formData.is_featured}
                  onChange={handleChange}
                  data-testid="news-media-featured-input"
                  className="w-4 h-4 accent-emerald-900"
                />
                <span className="text-sm font-medium text-stone-700">Featured</span>
              </label>
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-sm text-stone-600">Order:</label>
                <Input
                  type="number"
                  name="order"
                  value={formData.order}
                  onChange={handleChange}
                  className="w-20 h-9"
                  data-testid="news-media-order-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                data-testid="news-media-submit-button"
                className="flex-1 bg-emerald-900 hover:bg-emerald-800"
              >
                {editing ? 'UPDATE' : 'ADD NEWS / MEDIA'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                data-testid="news-media-cancel-button"
              >
                CANCEL
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNewsMedia;
